<template>
  <div class="hris-page">
    <div class="hris-page-inner">
      <div class="hris-page-header">
        <span class="hris-page-eyebrow">Human Resources</span>
        <h1 class="hris-page-title">Employee Onboarding</h1>
        <p class="hris-page-subtitle">Manage checklists, orientation schedules, and employment contracts for new hires.</p>
      </div>

      <div class="hris-panel" style="padding: 0; margin-bottom: 20px; overflow: hidden;">
        <q-tabs
          v-model="tab"
          active-color="green"
          indicator-color="green"
          align="left"
          inline-label
        >
          <q-tab name="checklists" label="Checklists & Tasks" icon="fas fa-tasks" />
          <q-tab name="orientations" label="Orientations" icon="fas fa-calendar-check" />
          <q-tab name="contracts" label="Contracts" icon="fas fa-file-contract" />
        </q-tabs>
      </div>

      <q-tab-panels v-model="tab" animated style="background: transparent" class="q-pa-none">
        <!-- CHECKLISTS TAB -->
        <q-tab-panel name="checklists" class="q-pa-none">
          <div class="hris-toolbar">
            <q-btn
              unelevated
              class="hris-q-primary"
              icon="fas fa-plus"
              label="Create Task"
              @click="openTaskDialog(null)"
            />
            <q-btn
              unelevated
              class="hris-q-outline"
              icon="fas fa-sync"
              label="Refresh"
              @click="getChecklists"
            />
            <div class="hris-toolbar-spacer"></div>
            <q-input
              v-model="checklistFilter"
              placeholder="Search tasks..."
              dense
              outlined
              color="green"
              style="min-width: 280px"
            >
              <template v-slot:append>
                <i class="fas fa-search"></i>
              </template>
            </q-input>
          </div>

          <div class="hris-panel">
            <q-table
              flat
              :data="checklists"
              :columns="checklistColumns"
              row-key="id"
              :filter="checklistFilter"
              :loading="loadingChecklists"
              separator="cell"
            >
              <template v-slot:loading>
                <div class="hris-loading absolute-full flex flex-center" style="background: rgba(255,255,255,0.7); z-index: 1;">
                  <q-spinner color="green" size="40px" />
                  <div>Loading onboarding checklists...</div>
                </div>
              </template>
              <template v-slot:no-data>
                <div class="hris-empty full-width">No onboarding tasks found.</div>
              </template>
              <template v-slot:body="props">
                <q-tr :props="props">
                  <q-td key="employee" :props="props">
                    <div class="text-weight-bold">
                      {{ props.row.employee ? props.row.employee.firstname + ' ' + props.row.employee.lastname : 'N/A' }}
                    </div>
                    <div class="text-caption text-grey-7">
                      ID: {{ props.row.employee ? props.row.employee.employee_id : 'N/A' }}
                    </div>
                  </q-td>
                  <q-td key="task_name" :props="props">{{ props.row.task_name }}</q-td>
                  <q-td key="description" :props="props" style="max-width: 250px; white-space: pre-wrap;">
                    {{ props.row.description || 'No description provided.' }}
                  </q-td>
                  <q-td key="coordinator" :props="props">
                    {{ props.row.coordinator ? props.row.coordinator.firstname + ' ' + props.row.coordinator.lastname : 'N/A' }}
                  </q-td>
                  <q-td key="due_date" :props="props">{{ formatDate(props.row.due_date) }}</q-td>
                  <q-td key="status" :props="props">
                    <q-chip
                      :color="getStatusColor(props.row.status)"
                      text-color="white"
                      dense
                    >
                      {{ props.row.status.toUpperCase().replace('_', ' ') }}
                    </q-chip>
                  </q-td>
                  <q-td key="completed_at" :props="props">{{ formatDate(props.row.completed_at) }}</q-td>
                  <q-td key="actions" :props="props" class="text-center">
                    <q-btn
                      flat
                      round
                      dense
                      class="hris-q-ghost"
                      icon="fas fa-edit"
                      @click="openTaskStatusDialog(props.row)"
                    >
                      <q-tooltip>Update Status</q-tooltip>
                    </q-btn>
                    <q-btn
                      flat
                      round
                      dense
                      class="hris-q-ghost"
                      icon="fas fa-trash-alt"
                      @click="confirmDeleteTask(props.row)"
                    >
                      <q-tooltip>Delete Task</q-tooltip>
                    </q-btn>
                  </q-td>
                </q-tr>
              </template>
            </q-table>
          </div>
        </q-tab-panel>

        <!-- ORIENTATIONS TAB -->
        <q-tab-panel name="orientations" class="q-pa-none">
          <div class="hris-toolbar">
            <q-btn
              unelevated
              class="hris-q-primary"
              icon="fas fa-plus"
              label="Schedule Orientation"
              @click="openOrientationDialog"
            />
            <q-btn
              unelevated
              class="hris-q-outline"
              icon="fas fa-sync"
              label="Refresh"
              @click="getOrientations"
            />
            <div class="hris-toolbar-spacer"></div>
            <q-input
              v-model="orientationFilter"
              placeholder="Search orientations..."
              dense
              outlined
              color="green"
              style="min-width: 280px"
            >
              <template v-slot:append>
                <i class="fas fa-search"></i>
              </template>
            </q-input>
          </div>

          <div class="hris-panel">
            <q-table
              flat
              :data="orientations"
              :columns="orientationColumns"
              row-key="id"
              :filter="orientationFilter"
              :loading="loadingOrientations"
              separator="cell"
            >
              <template v-slot:loading>
                <div class="hris-loading absolute-full flex flex-center" style="background: rgba(255,255,255,0.7); z-index: 1;">
                  <q-spinner color="green" size="40px" />
                  <div>Loading orientations...</div>
                </div>
              </template>
              <template v-slot:no-data>
                <div class="hris-empty full-width">No orientation schedules found.</div>
              </template>
              <template v-slot:body="props">
                <q-tr :props="props">
                  <q-td key="employee" :props="props">
                    <div class="text-weight-bold">
                      {{ props.row.employee ? props.row.employee.firstname + ' ' + props.row.employee.lastname : 'N/A' }}
                    </div>
                  </q-td>
                  <q-td key="title" :props="props">{{ props.row.title }}</q-td>
                  <q-td key="date_scheduled" :props="props">{{ formatDate(props.row.date_scheduled) }}</q-td>
                  <q-td key="location_details" :props="props">{{ props.row.location_details }}</q-td>
                  <q-td key="host" :props="props">
                    {{ props.row.host ? props.row.host.firstname + ' ' + props.row.host.lastname : 'N/A' }}
                  </q-td>
                  <q-td key="status" :props="props">
                    <q-chip
                      :color="getOrientationStatusColor(props.row.status)"
                      text-color="white"
                      dense
                    >
                      {{ props.row.status }}
                    </q-chip>
                  </q-td>
                  <q-td key="actions" :props="props" class="text-center">
                    <q-btn
                      flat
                      round
                      dense
                      class="hris-q-ghost"
                      icon="fas fa-comment-dots"
                      @click="openOrientationFeedbackDialog(props.row)"
                    >
                      <q-tooltip>Feedback / Update</q-tooltip>
                    </q-btn>
                  </q-td>
                </q-tr>
              </template>
            </q-table>
          </div>
        </q-tab-panel>

        <!-- CONTRACTS TAB -->
        <q-tab-panel name="contracts" class="q-pa-none">
          <div class="hris-toolbar">
            <q-btn
              unelevated
              class="hris-q-primary"
              icon="fas fa-file-signature"
              label="Generate Contract"
              @click="openContractDialog"
            />
            <q-btn
              unelevated
              class="hris-q-outline"
              icon="fas fa-sync"
              label="Refresh"
              @click="getContracts"
            />
            <div class="hris-toolbar-spacer"></div>
            <q-input
              v-model="contractFilter"
              placeholder="Search contracts..."
              dense
              outlined
              color="green"
              style="min-width: 280px"
            >
              <template v-slot:append>
                <i class="fas fa-search"></i>
              </template>
            </q-input>
          </div>

          <div class="hris-panel">
            <q-table
              flat
              :data="contracts"
              :columns="contractColumns"
              row-key="id"
              :filter="contractFilter"
              :loading="loadingContracts"
              separator="cell"
            >
              <template v-slot:loading>
                <div class="hris-loading absolute-full flex flex-center" style="background: rgba(255,255,255,0.7); z-index: 1;">
                  <q-spinner color="green" size="40px" />
                  <div>Loading contracts...</div>
                </div>
              </template>
              <template v-slot:no-data>
                <div class="hris-empty full-width">No employment contracts found.</div>
              </template>
              <template v-slot:body="props">
                <q-tr :props="props">
                  <q-td key="employee" :props="props">
                    <div class="text-weight-bold">
                      {{ props.row.employee ? props.row.employee.firstname + ' ' + props.row.employee.lastname : 'N/A' }}
                    </div>
                  </q-td>
                  <q-td key="template_name" :props="props">{{ props.row.template_name }}</q-td>
                  <q-td key="generated_at" :props="props">{{ formatDate(props.row.generated_at) }}</q-td>
                  <q-td key="status" :props="props">
                    <q-chip
                      :color="getContractStatusColor(props.row.status)"
                      text-color="white"
                      dense
                    >
                      {{ props.row.status }}
                    </q-chip>
                  </q-td>
                  <q-td key="signed_at" :props="props">{{ formatDate(props.row.signed_at) }}</q-td>
                  <q-td key="actions" :props="props" class="text-center">
                    <q-btn
                      flat
                      round
                      dense
                      class="hris-q-ghost"
                      icon="fas fa-edit"
                      @click="openContractStatusDialog(props.row)"
                    >
                      <q-tooltip>Update Status</q-tooltip>
                    </q-btn>
                  </q-td>
                </q-tr>
              </template>
            </q-table>
          </div>
        </q-tab-panel>
      </q-tab-panels>

      <!-- CREATE TASK DIALOG -->
      <q-dialog v-model="taskDialog" persistent transition-show="fade" transition-hide="fade">
        <div class="hris-modal">
          <div class="hris-modal-header">
            <div>
              <h2 class="hris-modal-title">Create Onboarding Task</h2>
            </div>
            <q-btn flat dense round v-close-popup class="hris-q-ghost" aria-label="Close">
              <i class="fas fa-times"></i>
            </q-btn>
          </div>
          <q-form @submit="saveTask">
            <div class="hris-modal-body">
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-select
                    v-model="taskForm.employee_id"
                    label="Select New Hire"
                    :options="employeeOptions"
                    option-value="id"
                    :option-label="(opt) => opt ? opt.firstname + ' ' + opt.lastname + ' (' + opt.employee_id + ')' : ''"
                    emit-value
                    map-options
                    outlined
                    dense
                    color="green"
                    use-input
                    @filter="filterEmployees"
                    :rules="[(val) => !!val || 'Employee is required']"
                  ></q-select>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-input
                    v-model="taskForm.task_name"
                    label="Task Name"
                    outlined
                    dense
                    color="green"
                    :rules="[(val) => !!val || 'Task name is required']"
                  ></q-input>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-input
                    v-model="taskForm.description"
                    label="Description"
                    type="textarea"
                    outlined
                    dense
                    color="green"
                  ></q-input>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-select
                    v-model="taskForm.assigned_to"
                    label="Assign Coordinator (Optional)"
                    :options="employeeOptions"
                    option-value="id"
                    :option-label="(opt) => opt ? opt.firstname + ' ' + opt.lastname : ''"
                    emit-value
                    map-options
                    outlined
                    dense
                    color="green"
                    use-input
                    @filter="filterEmployees"
                  ></q-select>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-input
                    v-model="taskForm.due_date"
                    label="Due Date"
                    outlined
                    dense
                    color="green"
                    mask="date"
                  >
                    <template v-slot:append>
                      <i class="far fa-calendar-alt cursor-pointer">
                        <q-popup-proxy ref="qDateTask" transition-show="scale" transition-hide="scale">
                          <q-date v-model="taskForm.due_date" @input="() => $refs.qDateTask.hide()" />
                        </q-popup-proxy>
                      </i>
                    </template>
                  </q-input>
                </div>
              </div>
            </div>
            <div class="hris-modal-footer">
              <q-btn unelevated class="hris-q-outline" label="Cancel" v-close-popup />
              <q-btn unelevated class="hris-q-primary" label="Save" type="submit" :loading="savingTask" />
            </div>
          </q-form>
        </div>
      </q-dialog>

      <!-- UPDATE TASK STATUS DIALOG -->
      <q-dialog v-model="taskStatusDialog" persistent transition-show="fade" transition-hide="fade">
        <div class="hris-modal">
          <div class="hris-modal-header">
            <div>
              <h2 class="hris-modal-title">Update Task Status</h2>
            </div>
            <q-btn flat dense round v-close-popup class="hris-q-ghost" aria-label="Close">
              <i class="fas fa-times"></i>
            </q-btn>
          </div>
          <q-form @submit="saveTaskStatus">
            <div class="hris-modal-body">
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-select
                    v-model="taskStatusForm.status"
                    label="Status"
                    :options="statusOptions"
                    outlined
                    dense
                    color="green"
                    :rules="[(val) => !!val || 'Status is required']"
                  ></q-select>
                </div>
              </div>
            </div>
            <div class="hris-modal-footer">
              <q-btn unelevated class="hris-q-outline" label="Cancel" v-close-popup />
              <q-btn unelevated class="hris-q-primary" label="Update" type="submit" :loading="savingTaskStatus" />
            </div>
          </q-form>
        </div>
      </q-dialog>

      <!-- ORIENTATION DIALOG -->
      <q-dialog v-model="orientationDialog" persistent transition-show="fade" transition-hide="fade">
        <div class="hris-modal">
          <div class="hris-modal-header">
            <div>
              <h2 class="hris-modal-title">Schedule Orientation</h2>
            </div>
            <q-btn flat dense round v-close-popup class="hris-q-ghost" aria-label="Close">
              <i class="fas fa-times"></i>
            </q-btn>
          </div>
          <q-form @submit="saveOrientation">
            <div class="hris-modal-body">
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-select
                    v-model="orientationForm.employee_id"
                    label="Select New Hire"
                    :options="employeeOptions"
                    option-value="id"
                    :option-label="(opt) => opt ? opt.firstname + ' ' + opt.lastname : ''"
                    emit-value
                    map-options
                    outlined
                    dense
                    color="green"
                    use-input
                    @filter="filterEmployees"
                    :rules="[(val) => !!val || 'Employee is required']"
                  ></q-select>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-input
                    v-model="orientationForm.title"
                    label="Title (e.g. Day 1 Welcome Orientation)"
                    outlined
                    dense
                    color="green"
                    :rules="[(val) => !!val || 'Title is required']"
                  ></q-input>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-input
                    v-model="orientationForm.date_scheduled"
                    label="Scheduled Date & Time"
                    outlined
                    dense
                    color="green"
                  >
                    <template v-slot:append>
                      <i class="far fa-calendar-alt cursor-pointer">
                        <q-popup-proxy ref="qDateTimeOri" transition-show="scale" transition-hide="scale">
                          <div class="q-gutter-sm q-pa-sm bg-white">
                            <q-date v-model="orientationForm.date_scheduled" mask="YYYY-MM-DD HH:mm:ss" />
                            <q-time v-model="orientationForm.date_scheduled" mask="YYYY-MM-DD HH:mm:ss" format24h />
                            <div class="row justify-end">
                              <q-btn label="OK" color="green" flat v-close-popup />
                            </div>
                          </div>
                        </q-popup-proxy>
                      </i>
                    </template>
                  </q-input>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-input
                    v-model="orientationForm.location_details"
                    label="Venue / Zoom Link"
                    outlined
                    dense
                    color="green"
                    :rules="[(val) => !!val || 'Location/Link is required']"
                  ></q-input>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-select
                    v-model="orientationForm.host_employee_id"
                    label="Host / Facilitator"
                    :options="employeeOptions"
                    option-value="id"
                    :option-label="(opt) => opt ? opt.firstname + ' ' + opt.lastname : ''"
                    emit-value
                    map-options
                    outlined
                    dense
                    color="green"
                    use-input
                    @filter="filterEmployees"
                    :rules="[(val) => !!val || 'Host is required']"
                  ></q-select>
                </div>
              </div>
            </div>
            <div class="hris-modal-footer">
              <q-btn unelevated class="hris-q-outline" label="Cancel" v-close-popup />
              <q-btn unelevated class="hris-q-primary" label="Schedule" type="submit" :loading="savingOrientation" />
            </div>
          </q-form>
        </div>
      </q-dialog>

      <!-- ORIENTATION FEEDBACK DIALOG -->
      <q-dialog v-model="orientationFeedbackDialog" persistent transition-show="fade" transition-hide="fade">
        <div class="hris-modal">
          <div class="hris-modal-header">
            <div>
              <h2 class="hris-modal-title">Orientation Feedback &amp; Status</h2>
            </div>
            <q-btn flat dense round v-close-popup class="hris-q-ghost" aria-label="Close">
              <i class="fas fa-times"></i>
            </q-btn>
          </div>
          <q-form @submit="saveOrientationFeedback">
            <div class="hris-modal-body">
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-select
                    v-model="orientationFeedbackForm.status"
                    label="Status"
                    :options="orientationStatusOptions"
                    outlined
                    dense
                    color="green"
                    :rules="[(val) => !!val || 'Status is required']"
                  ></q-select>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-input
                    v-model="orientationFeedbackForm.feedback"
                    label="Feedback Notes"
                    type="textarea"
                    outlined
                    dense
                    color="green"
                  ></q-input>
                </div>
              </div>
            </div>
            <div class="hris-modal-footer">
              <q-btn unelevated class="hris-q-outline" label="Cancel" v-close-popup />
              <q-btn unelevated class="hris-q-primary" label="Save" type="submit" :loading="savingOrientationFeedback" />
            </div>
          </q-form>
        </div>
      </q-dialog>

      <!-- CONTRACT GENERATION DIALOG -->
      <q-dialog v-model="contractDialog" persistent transition-show="fade" transition-hide="fade">
        <div class="hris-modal">
          <div class="hris-modal-header">
            <div>
              <h2 class="hris-modal-title">Generate Contract</h2>
            </div>
            <q-btn flat dense round v-close-popup class="hris-q-ghost" aria-label="Close">
              <i class="fas fa-times"></i>
            </q-btn>
          </div>
          <q-form @submit="saveContract">
            <div class="hris-modal-body">
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-select
                    v-model="contractForm.employee_id"
                    label="Select Employee"
                    :options="employeeOptions"
                    option-value="id"
                    :option-label="(opt) => opt ? opt.firstname + ' ' + opt.lastname : ''"
                    emit-value
                    map-options
                    outlined
                    dense
                    color="green"
                    use-input
                    @filter="filterEmployees"
                    :rules="[(val) => !!val || 'Employee is required']"
                  ></q-select>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-select
                    v-model="contractForm.template_name"
                    label="Contract Template"
                    :options="contractTemplateOptions"
                    outlined
                    dense
                    color="green"
                    :rules="[(val) => !!val || 'Template is required']"
                  ></q-select>
                </div>
              </div>
            </div>
            <div class="hris-modal-footer">
              <q-btn unelevated class="hris-q-outline" label="Cancel" v-close-popup />
              <q-btn unelevated class="hris-q-primary" label="Generate" type="submit" :loading="generatingContract" />
            </div>
          </q-form>
        </div>
      </q-dialog>

      <!-- CONTRACT STATUS DIALOG -->
      <q-dialog v-model="contractStatusDialog" persistent transition-show="fade" transition-hide="fade">
        <div class="hris-modal">
          <div class="hris-modal-header">
            <div>
              <h2 class="hris-modal-title">Update Contract Status</h2>
            </div>
            <q-btn flat dense round v-close-popup class="hris-q-ghost" aria-label="Close">
              <i class="fas fa-times"></i>
            </q-btn>
          </div>
          <q-form @submit="saveContractStatus">
            <div class="hris-modal-body">
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-select
                    v-model="contractStatusForm.status"
                    label="Status"
                    :options="contractStatusOptions"
                    outlined
                    dense
                    color="green"
                    :rules="[(val) => !!val || 'Status is required']"
                  ></q-select>
                </div>
              </div>
            </div>
            <div class="hris-modal-footer">
              <q-btn unelevated class="hris-q-outline" label="Cancel" v-close-popup />
              <q-btn unelevated class="hris-q-primary" label="Update" type="submit" :loading="savingContractStatus" />
            </div>
          </q-form>
        </div>
      </q-dialog>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { date } from "quasar";
import mixins from "../../mixins";

export default {
  mixins: [mixins],
  data() {
    return {
      tab: "checklists",
      checklists: [],
      orientations: [],
      contracts: [],
      employees: [],
      employeeOptions: [],

      // Filters
      checklistFilter: "",
      orientationFilter: "",
      contractFilter: "",

      // Loaders
      loadingChecklists: false,
      loadingOrientations: false,
      loadingContracts: false,

      // Dialog controls
      taskDialog: false,
      taskStatusDialog: false,
      orientationDialog: false,
      orientationFeedbackDialog: false,
      contractDialog: false,
      contractStatusDialog: false,

      // Form loads
      taskForm: {
        employee_id: null,
        task_name: "",
        description: "",
        assigned_to: null,
        due_date: "",
        status: "pending"
      },
      taskStatusForm: {
        id: null,
        status: ""
      },
      orientationForm: {
        employee_id: null,
        title: "",
        date_scheduled: "",
        location_details: "",
        host_employee_id: null
      },
      orientationFeedbackForm: {
        id: null,
        status: "",
        feedback: ""
      },
      contractForm: {
        employee_id: null,
        template_name: ""
      },
      contractStatusForm: {
        id: null,
        status: ""
      },

      // Saving indicators
      savingTask: false,
      savingTaskStatus: false,
      savingOrientation: false,
      savingOrientationFeedback: false,
      generatingContract: false,
      savingContractStatus: false,

      // Options
      statusOptions: ["pending", "in_progress", "completed", "waived"],
      orientationStatusOptions: ["scheduled", "completed", "cancelled", "no_show"],
      contractStatusOptions: ["draft", "sent", "signed", "void"],
      contractTemplateOptions: [
        "Full-Time Employment Agreement",
        "Part-Time Employment Agreement",
        "Executive Offer Contract",
        "Consultancy Agreement"
      ],

      // Table columns
      checklistColumns: [
        { name: "employee", align: "left", label: "Employee", sortable: true },
        { name: "task_name", align: "left", label: "Task", sortable: true },
        { name: "description", align: "left", label: "Description" },
        { name: "coordinator", align: "left", label: "Coordinator", sortable: true },
        { name: "due_date", align: "center", label: "Due Date", sortable: true },
        { name: "status", align: "center", label: "Status", sortable: true },
        { name: "completed_at", align: "center", label: "Completed At", sortable: true },
        { name: "actions", align: "center", label: "Actions" }
      ],
      orientationColumns: [
        { name: "employee", align: "left", label: "New Hire", sortable: true },
        { name: "title", align: "left", label: "Title", sortable: true },
        { name: "date_scheduled", align: "center", label: "Scheduled Date & Time", sortable: true },
        { name: "location_details", align: "left", label: "Location / Venue" },
        { name: "host", align: "left", label: "Host / Facilitator", sortable: true },
        { name: "status", align: "center", label: "Status", sortable: true },
        { name: "actions", align: "center", label: "Actions" }
      ],
      contractColumns: [
        { name: "employee", align: "left", label: "Employee", sortable: true },
        { name: "template_name", align: "left", label: "Contract Template", sortable: true },
        { name: "generated_at", align: "center", label: "Generated Date", sortable: true },
        { name: "status", align: "center", label: "Status", sortable: true },
        { name: "signed_at", align: "center", label: "Signed Date", sortable: true },
        { name: "actions", align: "center", label: "Actions" }
      ]
    };
  },
  created() {
    this.getChecklists();
    this.getOrientations();
    this.getContracts();
    this.getEmployees();
  },
  methods: {
   formatDate(d) {
      if (!d) return "N/A";
      return date.formatDate(d, "MMMM DD, YYYY");
    },
    getHeaders() {
      return {
        headers: { Authorization: "Bearer " + this.token }
      };
    },
    getChecklists() {
      this.loadingChecklists = true;
      axios
        .get(this.apiUrl + "/onboarding/checklists", this.getHeaders())
        .then((res) => {
          if (res.data.status) {
            this.checklists = res.data.data;
          }
        })
        .finally(() => {
          this.loadingChecklists = false;
        });
    },
    getOrientations() {
      this.loadingOrientations = true;
      axios
        .get(this.apiUrl + "/onboarding/orientations", this.getHeaders())
        .then((res) => {
          if (res.data.status) {
            this.orientations = res.data.data;
          }
        })
        .finally(() => {
          this.loadingOrientations = false;
        });
    },
    getContracts() {
      this.loadingContracts = true;
      axios
        .get(this.apiUrl + "/onboarding/contracts", this.getHeaders())
        .then((res) => {
          if (res.data.status) {
            this.contracts = res.data.data;
          }
        })
        .finally(() => {
          this.loadingContracts = false;
        });
    },
    getEmployees(filter = "") {
      axios
        .get(this.apiUrl + "/employees", {
          headers: { Authorization: "Bearer " + this.token },
          params: {
            status: "ACTIVE",
            countPerPage: 20,
            start: 0,
            filter
          }
        })
        .then((res) => {
          if (res.data.status) {
            const list = res.data.data || [];
            this.employees = list;
            this.employeeOptions = list;
          }
        });
    },
    filterEmployees(val, update) {
      update(() => {
        this.getEmployees(val);
      });
    },
    getStatusColor(status) {
      switch (status) {
        case "completed":
          return "positive";
        case "in_progress":
          return "warning";
        case "waived":
          return "grey";
        default:
          return "negative";
      }
    },
    getOrientationStatusColor(status) {
      switch (status) {
        case "completed":
          return "positive";
        case "cancelled":
          return "red-5";
        case "no_show":
          return "red";
        default:
          return "primary";
      }
    },
    getContractStatusColor(status) {
      switch (status) {
        case "signed":
          return "positive";
        case "sent":
          return "blue";
        case "void":
          return "grey";
        default:
          return "orange";
      }
    },

    // --- CHECKLIST OPERATIONS ---
    openTaskDialog() {
      this.taskForm = {
        employee_id: null,
        task_name: "",
        description: "",
        assigned_to: null,
        due_date: "",
        status: "pending"
      };
      this.taskDialog = true;
    },
    saveTask() {
      this.savingTask = true;
      axios
        .post(this.apiUrl + "/onboarding/checklist/create", this.taskForm, this.getHeaders())
        .then((res) => {
          if (res.data.status) {
            this.$q.notify({ type: "positive", message: "Task created successfully." });
            this.taskDialog = false;
            this.getChecklists();
          } else {
            this.$q.notify({ type: "negative", message: res.data.message || "Failed to create task." });
          }
        })
        .catch(() => {
          this.$q.notify({ type: "negative", message: "Error communicating with server." });
        })
        .finally(() => {
          this.savingTask = false;
        });
    },
    openTaskStatusDialog(row) {
      this.taskStatusForm = {
        id: row.id,
        status: row.status
      };
      this.taskStatusDialog = true;
    },
    saveTaskStatus() {
      this.savingTaskStatus = true;
      axios
        .post(this.apiUrl + "/onboarding/checklist/update", this.taskStatusForm, this.getHeaders())
        .then((res) => {
          if (res.data.status) {
            this.$q.notify({ type: "positive", message: "Status updated successfully." });
            this.taskStatusDialog = false;
            this.getChecklists();
          }
        })
        .finally(() => {
          this.savingTaskStatus = false;
        });
    },
    confirmDeleteTask(row) {
      this.$q
        .dialog({
          title: "Confirm Deletion",
          message: `Are you sure you want to delete the task "${row.task_name}"?`,
          cancel: true,
          persistent: true
        })
        .onOk(() => {
          axios
            .post(this.apiUrl + `/onboarding/checklist/delete/${row.id}`, {}, this.getHeaders())
            .then((res) => {
              if (res.data.status) {
                this.$q.notify({ type: "positive", message: "Task deleted." });
                this.getChecklists();
              }
            });
        });
    },

    // --- ORIENTATION OPERATIONS ---
    openOrientationDialog() {
      this.orientationForm = {
        employee_id: null,
        title: "",
        date_scheduled: "",
        location_details: "",
        host_employee_id: null
      };
      this.orientationDialog = true;
    },
    saveOrientation() {
      this.savingOrientation = true;
      axios
        .post(this.apiUrl + "/onboarding/orientation/schedule", this.orientationForm, this.getHeaders())
        .then((res) => {
          if (res.data.status) {
            this.$q.notify({ type: "positive", message: "Orientation scheduled." });
            this.orientationDialog = false;
            this.getOrientations();
          }
        })
        .finally(() => {
          this.savingOrientation = false;
        });
    },
    openOrientationFeedbackDialog(row) {
      this.orientationFeedbackForm = {
        id: row.id,
        status: row.status,
        feedback: row.feedback || ""
      };
      this.orientationFeedbackDialog = true;
    },
    saveOrientationFeedback() {
      this.savingOrientationFeedback = true;
      axios
        .post(this.apiUrl + "/onboarding/orientation/update", this.orientationFeedbackForm, this.getHeaders())
        .then((res) => {
          if (res.data.status) {
            this.$q.notify({ type: "positive", message: "Orientation updated." });
            this.orientationFeedbackDialog = false;
            this.getOrientations();
          }
        })
        .finally(() => {
          this.savingOrientationFeedback = false;
        });
    },

    // --- CONTRACT OPERATIONS ---
    openContractDialog() {
      this.contractForm = {
        employee_id: null,
        template_name: ""
      };
      this.contractDialog = true;
    },
    saveContract() {
      this.generatingContract = true;
      axios
        .post(this.apiUrl + "/onboarding/contract/generate", this.contractForm, this.getHeaders())
        .then((res) => {
          if (res.data.status) {
            this.$q.notify({ type: "positive", message: "Contract generated successfully." });
            this.contractDialog = false;
            this.getContracts();
          }
        })
        .finally(() => {
          this.generatingContract = false;
        });
    },
    openContractStatusDialog(row) {
      this.contractStatusForm = {
        id: row.id,
        status: row.status
      };
      this.contractStatusDialog = true;
    },
    saveContractStatus() {
      this.savingContractStatus = true;
      axios
        .post(this.apiUrl + "/onboarding/contract/update", this.contractStatusForm, this.getHeaders())
        .then((res) => {
          if (res.data.status) {
            this.$q.notify({ type: "positive", message: "Contract updated." });
            this.contractStatusDialog = false;
            this.getContracts();
          }
        })
        .finally(() => {
          this.savingContractStatus = false;
        });
    }
  }
};
</script>
